import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Play, RotateCcw } from 'lucide-react';
import { learnApi } from '@/api/endpoints';
import { getApiErrorMessage } from '@/api/client';
import type { ArticleResultData } from '@/api/types';
import { ResultTable } from '@/components/editor/ResultTable';
import { trackEvent } from '@/lib/analytics';
import { useAuthStore } from '@/store/auth';
import type { QueryResult } from '@/types';

interface Props {
  articleSlug: string;
  blockId: string;
  query: string;
  result?: ArticleResultData | null;
}

interface RunState {
  result: ArticleResultData;
  truncated: boolean;
}

/**
 * Tahrirlanadigan misol. Tayyor natija HTML da turadi; server faqat so'rov o'zgartirilgach chaqiriladi
 * ("Ishga tushirish" o'zgarishsiz so'rovda o'chiq). Setup SQL serverda, blok id si bo'yicha olinadi.
 */
export function SqlEditorBlock({ articleSlug, blockId, query: initialQuery, result: initialResult }: Props) {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [query, setQuery] = useState(initialQuery);
  const [run, setRun] = useState<RunState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [promptShown, setPromptShown] = useState(false);

  const edited = query.trim() !== initialQuery.trim();
  const shown = edited ? run?.result : (initialResult ?? run?.result);
  const props = { slug: articleSlug, blockId };

  const execute = async () => {
    if (!edited || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await learnApi.run(articleSlug, blockId, query);
      if (res.data.error) {
        setRun(null);
        setError(res.data.error);
        trackEvent('article_query_error', props);
        return;
      }
      setRun({ result: res.data, truncated: res.data.truncated });
      trackEvent('article_query_run', props);
      if (!isAuthenticated && !promptShown) {
        setPromptShown(true);
        trackEvent('learn_signup_prompt_shown', props);
      }
    } catch (err) {
      setRun(null);
      setError(getApiErrorMessage(err, t('learn.runFailed')));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setQuery(initialQuery);
    setRun(null);
    setError(null);
  };

  return (
    <div className="card my-5 overflow-hidden">
      <textarea
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            void execute();
          }
        }}
        spellCheck={false}
        rows={Math.min(12, Math.max(3, query.split('\n').length))}
        aria-label={t('learn.editorLabel')}
        className="block w-full resize-y border-0 bg-surface p-4 font-mono text-sm text-content outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
      />
      <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-2.5">
        <button
          type="button"
          onClick={() => void execute()}
          disabled={!edited || loading}
          className="btn btn-primary inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Play className="h-4 w-4" aria-hidden />}
          {t('learn.run')}
        </button>
        {edited && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1.5 text-sm text-content-secondary transition-colors hover:text-content"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            {t('learn.runReset')}
          </button>
        )}
        {!edited && <span className="text-xs text-content-muted">{t('learn.runHint')}</span>}
      </div>

      <div aria-live="polite" className="border-t border-line p-4 empty:hidden">
        {error ? (
          <p role="alert" className="whitespace-pre-wrap break-words font-mono text-sm text-[var(--hard)]">
            {error}
          </p>
        ) : shown ? (
          <>
            <ResultTable result={shown as QueryResult} />
            {edited && run?.truncated && <p className="mt-2 text-xs text-content-muted">{t('learn.runTruncated')}</p>}
          </>
        ) : null}
        {promptShown && !error && (
          <p className="mt-3 text-sm text-content-secondary">
            {t('learn.signupPrompt')}{' '}
            <Link
              to="/register"
              onClick={() => trackEvent('learn_signup_prompt_clicked', props)}
              className="font-semibold text-brand-600 hover:underline"
            >
              {t('learn.signupPromptCta')}
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
